import React, { useEffect, useState, useRef } from 'react'
import { X, Plus, Trash2, GripVertical, Clock, ChevronDown, ChevronUp, Save, Edit3, BookOpen } from 'lucide-react'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'

interface GuidelineSection {
  id: string
  heading: string
  content: string
}

interface Props {
  clientId: string
  clientName: string
  open: boolean
  onClose: () => void
  readOnly?: boolean
}

export default function ClientGuideline({ clientId, clientName, open, onClose, readOnly = false }: Props) {
  const { hasPermission } = useAuth()
  const canEdit = !readOnly && hasPermission('edit_client_guideline')

  const [sections, setSections] = useState<GuidelineSection[]>([])
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showVersions, setShowVersions] = useState(false)
  const [versions, setVersions] = useState<any[]>([])
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [versionNote, setVersionNote] = useState('')
  const [showVersionNote, setShowVersionNote] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<any>(null)
  const [isEmpty, setIsEmpty] = useState(true)
  const drawerRef = useRef<HTMLDivElement>(null)

  const defaultSections: GuidelineSection[] = [
    { id: 'file-formats', heading: 'File Formats', content: '' },
    { id: 'color-preferences', heading: 'Color Preferences', content: '' },
    { id: 'vendor-info', heading: 'Vendor Information', content: '' },
    { id: 'special-instructions', heading: 'Special Instructions', content: '' },
  ]

  useEffect(() => {
    if (open && clientId) fetchGuideline()
  }, [open, clientId])

  const fetchGuideline = async () => {
    setLoading(true)
    try {
      const res = await api.get(`/guideline/${clientId}`)
      if (res.data) {
        // content may come as string or array depending on Prisma JSON serialization
        let content = res.data.content
        if (typeof content === 'string') {
          try { content = JSON.parse(content) } catch { content = [] }
        }
        if (Array.isArray(content) && content.length > 0) {
          setSections(content)
          setLastUpdated(res.data)
          setIsEmpty(false)
        } else {
          setSections(defaultSections)
          setLastUpdated(null)
          setIsEmpty(true)
        }
      } else {
        setSections(defaultSections)
        setLastUpdated(null)
        setIsEmpty(true)
      }
    } catch {
      setSections(defaultSections)
      setIsEmpty(true)
    }
    setLoading(false)
  }

  const fetchVersions = async () => {
    setVersionsLoading(true)
    try {
      const res = await api.get(`/guideline/${clientId}/versions`)
      setVersions(res.data)
    } catch {}
    setVersionsLoading(false)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await api.put(`/guideline/${clientId}`, {
        content: sections,
        versionNote: versionNote.trim() || null
      })
      setLastUpdated(res.data)
      setIsEmpty(false)
      setEditing(false)
      setShowVersionNote(false)
      setVersionNote('')
    } catch {}
    setSaving(false)
  }

  const addSection = () => {
    setSections(prev => [...prev, {
      id: `section-${Date.now()}`,
      heading: 'New Section',
      content: ''
    }])
  }

  const updateSection = (id: string, field: 'heading' | 'content', value: string) => {
    setSections(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s))
  }

  const removeSection = (id: string) => {
    setSections(prev => prev.filter(s => s.id !== id))
  }

  const dragItem = useRef<number | null>(null)
  const dragOver = useRef<number | null>(null)

  const handleDragStart = (index: number) => {
    dragItem.current = index
  }

  const handleDragEnter = (index: number) => {
    dragOver.current = index
  }

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOver.current === null) return
    const updated = [...sections]
    const dragged = updated.splice(dragItem.current, 1)[0]
    updated.splice(dragOver.current, 0, dragged)
    setSections(updated)
    dragItem.current = null
    dragOver.current = null
  }

  const restoreVersion = (version: any) => {
    if (!window.confirm('Restore this version? Current content will be saved as a new version.')) return
    setSections(version.content)
    setEditing(true)
    setShowVersions(false)
  }

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        if (!editing) onClose()
      }
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, editing])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />

      {/* Drawer */}
      <div ref={drawerRef}
        className="relative w-full max-w-xl h-full bg-white dark:bg-[#0d1e35] shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
              <BookOpen size={15} className="text-orange-500" />
            </div>
            <div>
              <p className="font-semibold text-navy-900 dark:text-white text-sm">Client Guideline</p>
              <p className="text-xs text-gray-400 truncate max-w-[200px]">{clientName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && !editing && (
              <button onClick={() => setEditing(true)}
                className="flex items-center gap-1.5 text-xs text-orange-500 hover:text-orange-600 bg-orange-50 dark:bg-orange-900/10 px-3 py-1.5 rounded-lg font-medium transition-colors">
                <Edit3 size={12} /> Edit
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Last updated bar */}
        {lastUpdated && (
          <div className="px-6 py-2 bg-gray-50 dark:bg-[#0a1628] border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
            <p className="text-[10px] text-gray-400 flex items-center gap-1">
              <Clock size={10} />
              Last updated by <span className="font-medium text-gray-500">{lastUpdated.updatedByUser?.name || 'Unknown'}</span> · {new Date(lastUpdated.updatedAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
            <button onClick={() => { setShowVersions(v => !v); if (!showVersions) fetchVersions() }}
              className="text-[10px] text-orange-500 hover:text-orange-600 flex items-center gap-0.5 font-medium">
              History {showVersions ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            </button>
          </div>
        )}

        {/* Version history panel */}
        {showVersions && (
          <div className="px-6 py-3 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-100 dark:border-amber-900/20 max-h-48 overflow-y-auto">
            <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide mb-2">Version History</p>
            {versionsLoading ? (
              <p className="text-xs text-gray-400">Loading...</p>
            ) : versions.length === 0 ? (
              <p className="text-xs text-gray-400">No previous versions</p>
            ) : (
              <div className="space-y-2">
                {versions.map((v, i) => (
                  <div key={v.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-gray-600 dark:text-gray-300">
                        {new Date(v.updatedAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {v.updatedByUser && <span className="text-gray-400"> · {v.updatedByUser.name}</span>}
                      </p>
                      {v.versionNote && <p className="text-[10px] text-amber-600 italic">"{v.versionNote}"</p>}
                    </div>
                    {canEdit && (
                      <button onClick={() => restoreVersion(v)}
                        className="text-[10px] text-orange-500 hover:text-orange-600 font-medium">
                        Restore
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-[#1a3050] rounded w-1/3 mb-2" />
                  <div className="h-16 bg-gray-100 dark:bg-[#162840] rounded" />
                </div>
              ))}
            </div>
          ) : isEmpty && !editing ? (
            <div className="flex flex-col items-center justify-center h-full py-16 text-center">
              <BookOpen size={40} className="text-gray-200 dark:text-gray-700 mb-3" />
              <p className="text-gray-400 text-sm mb-1">No guideline yet for this client</p>
              {canEdit && (
                <button onClick={() => setEditing(true)}
                  className="mt-3 text-xs text-orange-500 hover:text-orange-600 font-medium bg-orange-50 dark:bg-orange-900/10 px-4 py-2 rounded-lg">
                  Create Guideline
                </button>
              )}
            </div>
          ) : (
            sections.map((section, idx) => (
              <div key={section.id} className="group"
                draggable={editing}
                onDragStart={() => handleDragStart(idx)}
                onDragEnter={() => handleDragEnter(idx)}
                onDragEnd={handleDragEnd}
                onDragOver={e => e.preventDefault()}
                style={{ opacity: dragItem.current === idx ? 0.5 : 1, cursor: editing ? 'grab' : 'default' }}>
                {editing ? (
                  <div className="border border-gray-200 dark:border-[#1e3a5f] rounded-xl overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-[#162840] border-b border-gray-200 dark:border-[#1e3a5f]">
                      <GripVertical size={14} className="text-gray-300 cursor-grab" />
                      <input
                        value={section.heading}
                        onChange={e => updateSection(section.id, 'heading', e.target.value)}
                        className="flex-1 bg-transparent text-sm font-semibold text-navy-800 dark:text-white focus:outline-none"
                        placeholder="Section heading..."
                      />
                      <button onClick={() => removeSection(section.id)}
                        className="text-gray-300 hover:text-red-400 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <textarea
                      value={section.content}
                      onChange={e => updateSection(section.id, 'content', e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2.5 text-sm text-navy-800 dark:text-gray-200 bg-white dark:bg-[#0d1e35] focus:outline-none resize-none placeholder-gray-300"
                      placeholder="Add guideline content for this section..."
                    />
                  </div>
                ) : (
                  <div>
                    <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1.5">{section.heading}</p>
                    {section.content ? (
                      <p className="text-sm text-navy-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">{section.content}</p>
                    ) : (
                      <p className="text-sm text-gray-300 dark:text-gray-600 italic">No content added</p>
                    )}
                    {idx < sections.length - 1 && <hr className="mt-4 border-gray-100 dark:border-[#1e3a5f]" />}
                  </div>
                )}
              </div>
            ))
          )}

          {editing && (
            <button onClick={addSection}
              className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-200 dark:border-[#1e3a5f] rounded-xl text-sm text-gray-400 hover:text-orange-500 hover:border-orange-300 transition-colors">
              <Plus size={14} /> Add Section
            </button>
          )}
        </div>

        {/* Footer — save/cancel */}
        {editing && (
          <div className="px-6 py-4 border-t border-gray-100 dark:border-[#1e3a5f] flex-shrink-0 space-y-3">
            {showVersionNote ? (
              <input
                value={versionNote}
                onChange={e => setVersionNote(e.target.value)}
                className="w-full border border-gray-200 dark:border-[#1e3a5f] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400 dark:bg-[#162840] dark:text-white"
                placeholder="Version note (optional) e.g. Updated vendor info..."
              />
            ) : (
              <button onClick={() => setShowVersionNote(true)}
                className="text-xs text-gray-400 hover:text-gray-600">
                + Add version note
              </button>
            )}
            <div className="flex gap-2">
              <button onClick={() => { setEditing(false); fetchGuideline() }}
                className="flex-1 border border-gray-200 dark:border-[#1e3a5f] text-gray-500 dark:text-gray-400 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-[#162840] transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors">
                <Save size={14} /> {saving ? 'Saving...' : 'Save Guideline'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}